import { Checkbox, Flex, Text } from '@byecode/ui'
import type { ApplicationSettingBrowserOperation } from '@lighthouse/core'
import { SpaceVersion } from '@lighthouse/core'
import { spaceVersionEnum, useAtomAction, useAtomData, useSpaceQuotaContext } from '@lighthouse/shared'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { debounce } from 'throttle-debounce'

import { updateAppAtom } from '@/atoms/application/action'
import { applicationSettingAtom } from '@/atoms/application/state'
import { openSpaceGradeConfirm, SpaceGradeTag } from '@/components/SpaceGrade'

import { SettingCard } from '../SettingCard'

interface SettingBrowserOperationProps {}

const SettingBrowserOperation: React.FunctionComponent<SettingBrowserOperationProps> = props => {
    const webApp = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.browserOperation, [])
    )
    const { quota } = useSpaceQuotaContext()

    const { run: updateAppSetting } = useAtomAction(updateAppAtom)
    const methods = useForm<ApplicationSettingBrowserOperation>({
        mode: 'onChange',
        defaultValues: webApp
    })
    const { watch, control } = methods

    const handleSubmit = useMemo(
        () =>
            debounce(500, async (value: ApplicationSettingBrowserOperation) => {
                const isSuccess = await updateAppSetting({
                    config: {
                        browserOperation: value
                    }
                })
            }),
        [updateAppSetting]
    )

    useEffect(() => {
        const { unsubscribe } = watch((value, { name, type }) => {
            handleSubmit(value as ApplicationSettingBrowserOperation)
        })
        return unsubscribe
    }, [handleSubmit, updateAppSetting, watch])

    return (
        <SettingCard
            title={
                <Flex alignItems="center" gap={8}>
                    <Text>浏览器操作限制</Text>
                    <SpaceGradeTag value={spaceVersionEnum.ADVANCED} />
                </Flex>
            }
            describe="禁用浏览器某些操作限制(包括电脑端和手机端)，该设置对所有页面中的组件生效"
        >
            <Flex direction="column" gap={16} pt={16}>
                <Controller
                    control={control}
                    name="disableBrowserMenu"
                    render={({ field }) => (
                        <Checkbox
                            size="sm"
                            label="禁用浏览器右侧菜单"
                            checked={field.value}
                            onChange={v => {
                                if (
                                    !field.value &&
                                    quota &&
                                    quota?.currentVersionCode !== undefined &&
                                    quota.currentVersionCode < spaceVersionEnum.ADVANCED
                                ) {
                                    v.preventDefault()
                                    field.onChange(field.value)
                                    openSpaceGradeConfirm(SpaceVersion.ADVANCED)
                                    return
                                }

                                field.onChange(v)
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="disableSelectText"
                    render={({ field }) => (
                        <Checkbox
                            size="sm"
                            label="禁止选择文本"
                            checked={field.value}
                            onChange={v => {
                                if (
                                    !field.value &&
                                    quota &&
                                    quota?.currentVersionCode !== undefined &&
                                    quota.currentVersionCode < spaceVersionEnum.ADVANCED
                                ) {
                                    v.preventDefault()
                                    field.onChange(field.value)
                                    openSpaceGradeConfirm(SpaceVersion.ADVANCED)
                                    return
                                }

                                field.onChange(v)
                            }}
                        />
                    )}
                />
            </Flex>
        </SettingCard>
    )
}

export default SettingBrowserOperation
